import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deleteTableReplay, saveTableReplay } from "../api/tableReplayApi";
import { isInstantReplayEnabled } from "../utils/liveTrainingSettings";

/** Phones re-announce every 5s while recording; drop a table after missed beats. */
const EVAL_RECORDING_STALE_MS = 15000;

function replayFailureMessage(code, serverMessage) {
  const text = typeof serverMessage === "string" ? serverMessage.trim() : "";
  if (text) return text;
  switch (code) {
    case "no_camera":
      return "No player camera is connected at this table.";
    case "CONSENT_REQUIRED":
      return "No player at this table has video consent for replay.";
    case "exercise_not_started":
      return "Start the exercise to replay the last 10 seconds.";
    case "coach_required":
      return "Only the coach can request a replay.";
    case "buffer_empty":
    case "timeout":
      return "Replay is not ready yet. Keep the camera on the table a few more seconds.";
    case "not_live":
      return "Start the exercise to replay the last 10 seconds.";
    case "eval_recording":
      return "Replay is off while the evaluation video records. Try again once it ends.";
    default:
      return "Replay didn't start. Try again.";
  }
}

/**
 * Coach asks a table or player camera for the last 10 seconds. Table requests
 * fan out to every recording camera; clips play in a vertical scroll feed.
 * Requests are only accepted while the training exercise is started or paused.
 */
export function useCoachTableReplay({
  socket,
  teId,
  tsId,
  coachUserId,
  exerciseLive = false,
}) {
  const [armedByTable, setArmedByTable] = useState({});
  const [pendingByTable, setPendingByTable] = useState({});
  const [noticeByTable, setNoticeByTable] = useState({});
  const [clips, setClips] = useState([]);
  const [busy, setBusy] = useState(false);
  /** table → camera_id → last heartbeat ms, for phones recording the eval video. */
  const [evalCamerasByTable, setEvalCamerasByTable] = useState({});
  /** @type {React.MutableRefObject<Record<string, { expected: number|null, settled: number }>>} */
  const awaitingByTableRef = useRef({});
  const enabled = isInstantReplayEnabled(tsId) && Boolean(exerciseLive);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Finalized / not running: drop every replay trace, including the review popup.
  useEffect(() => {
    if (enabled) return;
    awaitingByTableRef.current = {};
    setArmedByTable({});
    setPendingByTable({});
    setNoticeByTable({});
    setClips([]);
    setEvalCamerasByTable({});
  }, [enabled, teId]);

  useEffect(() => {
    if (!socket) return undefined;
    const onEvalRecording = (payload) => {
      if (payload?.table_number == null) return;
      if (tsId && payload.ts_id && String(payload.ts_id) !== String(tsId)) return;
      const key = String(payload.table_number);
      const camera = String(payload.camera_id || "camera");
      setEvalCamerasByTable((prev) => {
        const cams = { ...(prev[key] || {}) };
        if (payload.recording) cams[camera] = Date.now();
        else delete cams[camera];
        const next = { ...prev };
        if (Object.keys(cams).length) next[key] = cams;
        else delete next[key];
        return next;
      });
    };
    socket.on("tableEvalRecording", onEvalRecording);
    return () => socket.off("tableEvalRecording", onEvalRecording);
  }, [socket, tsId]);

  const hasEvalCameras = Object.keys(evalCamerasByTable).length > 0;
  useEffect(() => {
    if (!hasEvalCameras) return undefined;
    const timer = window.setInterval(() => {
      const cutoff = Date.now() - EVAL_RECORDING_STALE_MS;
      setEvalCamerasByTable((prev) => {
        let changed = false;
        const next = {};
        for (const [key, cams] of Object.entries(prev)) {
          const live = Object.fromEntries(
            Object.entries(cams).filter(([, seen]) => seen >= cutoff)
          );
          if (Object.keys(live).length !== Object.keys(cams).length) changed = true;
          if (Object.keys(live).length) next[key] = live;
        }
        return changed ? next : prev;
      });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [hasEvalCameras]);

  const evalRecordingByTable = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(evalCamerasByTable).map((key) => [key, true])
      ),
    [evalCamerasByTable]
  );

  const syncPendingFlag = useCallback((tableNumber) => {
    const key = String(tableNumber);
    const state = awaitingByTableRef.current[key];
    const stillWaiting =
      !state ||
      state.expected == null ||
      state.settled < state.expected;
    setPendingByTable((prev) => {
      const next = stillWaiting ? "pending" : null;
      if (prev[key] === next) return prev;
      return { ...prev, [key]: next };
    });
  }, []);

  const setTableNotice = useCallback((tableNumber, message) => {
    if (tableNumber == null) return;
    const key = String(tableNumber);
    setNoticeByTable((prev) => {
      const next = message || null;
      if (prev[key] === next) return prev;
      return { ...prev, [key]: next };
    });
  }, []);

  const beginTableAwaiting = useCallback((tableNumber) => {
    const key = String(tableNumber);
    awaitingByTableRef.current[key] = { expected: 1, settled: 0 };
    setPendingByTable((prev) => ({ ...prev, [key]: "pending" }));
    setTableNotice(tableNumber, null);
  }, [setTableNotice]);

  const setTableExpected = useCallback(
    (tableNumber, cameras) => {
      const key = String(tableNumber);
      const prev = awaitingByTableRef.current[key] || {
        expected: null,
        settled: 0,
      };
      awaitingByTableRef.current[key] = {
        ...prev,
        expected: Math.max(1, Number(cameras) || 1),
      };
      syncPendingFlag(tableNumber);
    },
    [syncPendingFlag]
  );

  const settleTableCamera = useCallback(
    (tableNumber) => {
      if (tableNumber == null) return;
      const key = String(tableNumber);
      const prev = awaitingByTableRef.current[key] || {
        expected: null,
        settled: 0,
      };
      awaitingByTableRef.current[key] = {
        ...prev,
        settled: prev.settled + 1,
      };
      syncPendingFlag(tableNumber);
    },
    [syncPendingFlag]
  );

  useEffect(() => {
    if (!socket) return undefined;
    const onArmed = (payload) => {
      if (!enabledRef.current || payload?.table_number == null) return;
      if (tsId && payload.ts_id && String(payload.ts_id) !== String(tsId)) return;
      setArmedByTable((prev) => ({
        ...prev,
        [String(payload.table_number)]: true,
      }));
    };
    const onPending = (payload) => {
      if (!enabledRef.current || payload?.table_number == null) return;
      const cameras =
        Number(payload.cameras) ||
        (Array.isArray(payload.replay_ids) ? payload.replay_ids.length : 0) ||
        1;
      setTableExpected(payload.table_number, cameras);
    };
    const onReady = (payload) => {
      if (!enabledRef.current || !payload?.message_id) return;
      if (tsId && payload.ts_id && String(payload.ts_id) !== String(tsId)) return;
      settleTableCamera(payload.table_number);
      setTableNotice(payload.table_number, null);
      setClips((prev) => {
        if (
          payload.message_id &&
          prev.some((row) => row?.message_id === payload.message_id)
        ) {
          return prev;
        }
        return [...prev, payload];
      });
    };
    const onFailed = (payload) => {
      if (!enabledRef.current || payload?.table_number == null) return;
      settleTableCamera(payload.table_number);
      setTableNotice(
        payload.table_number,
        replayFailureMessage(payload.code, payload.message)
      );
    };
    socket.on("tableReplayArmed", onArmed);
    socket.on("tableReplayPending", onPending);
    socket.on("tableReplayReady", onReady);
    socket.on("tableReplayFailed", onFailed);
    return () => {
      socket.off("tableReplayArmed", onArmed);
      socket.off("tableReplayPending", onPending);
      socket.off("tableReplayReady", onReady);
      socket.off("tableReplayFailed", onFailed);
    };
  }, [socket, tsId, setTableExpected, settleTableCamera, setTableNotice]);

  const requestReplay = useCallback(
    ({ tableNumber }) => {
      if (!socket || !teId || !tsId || tableNumber == null || !enabled) {
        if (tableNumber != null) {
          setTableNotice(
            tableNumber,
            replayFailureMessage(
              !enabled ? "not_live" : "unavailable"
            )
          );
        }
        return;
      }
      beginTableAwaiting(tableNumber);
      socket.emit(
        "requestTableReplay",
        {
          te_id: teId,
          ts_id: tsId,
          table_number: tableNumber,
          coach_id: coachUserId || null,
        },
        (ack) => {
          if (!ack || ack.ok === false) {
            awaitingByTableRef.current[String(tableNumber)] = {
              expected: 0,
              settled: 0,
            };
            setPendingByTable((prev) => ({
              ...prev,
              [String(tableNumber)]: null,
            }));
            setTableNotice(
              tableNumber,
              replayFailureMessage(ack?.code, ack?.message)
            );
            return;
          }
          const cameras =
            Number(ack?.cameras) ||
            (Array.isArray(ack?.replay_ids) ? ack.replay_ids.length : 0) ||
            1;
          setTableExpected(tableNumber, cameras);
        }
      );
    },
    [
      socket,
      teId,
      tsId,
      coachUserId,
      enabled,
      beginTableAwaiting,
      setTableExpected,
      setTableNotice,
    ]
  );

  const removeClip = useCallback((messageId) => {
    if (!messageId) return;
    setClips((prev) => prev.filter((row) => row?.message_id !== messageId));
  }, []);

  const closeReview = useCallback(() => {
    // Skip current clip (leave ephemeral to expire); show the next camera if any.
    setClips((prev) => prev.slice(1));
  }, []);

  const saveClip = useCallback(
    async (messageId) => {
      if (!messageId) return;
      setBusy(true);
      try {
        await saveTableReplay(messageId);
        removeClip(messageId);
      } catch (err) {
        console.warn("save table replay failed:", err?.message || err);
        const clip = clips.find((row) => row?.message_id === messageId);
        if (clip?.table_number != null) {
          setTableNotice(
            clip.table_number,
            err?.message || "Could not save replay as player feedback."
          );
        }
      } finally {
        setBusy(false);
      }
    },
    [removeClip, clips, setTableNotice]
  );

  const deleteClip = useCallback(
    async (messageId) => {
      if (!messageId) return;
      setBusy(true);
      try {
        await deleteTableReplay(messageId);
        removeClip(messageId);
      } catch (err) {
        console.warn("delete table replay failed:", err?.message || err);
      } finally {
        setBusy(false);
      }
    },
    [removeClip]
  );

  return {
    enabled,
    armedByTable,
    evalRecordingByTable,
    pendingByTable,
    noticeByTable,
    clips,
    busy,
    requestReplay,
    closeReview,
    saveClip,
    deleteClip,
  };
}
